/**
 * Five Paisa OpenAPI option greeks WebSocket (gateway).
 * URL: wss://gateway.5paisa.com/openapi/greeks?access_token=...
 * Subscribe body: { Method, Operation: "optiongreek", instruments: string[] }
 */
class FivePaisaOptionGreeksWebSocketService {
  constructor() {
    this.ws = null;
    this.accessToken = null;

    this.reconnectTimeout = null;
    this.connectTimeout = null;

    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 20;
    this.baseReconnectDelayMs = 1000;
    this.maxReconnectDelayMs = 15000;
    this.connectTimeoutMs = 12000;

    this.statusListeners = new Set();
    this.greeksListeners = new Set();
    this.errorListeners = new Set();

    /** @type {Map<string, string>} instrumentId -> instrumentId */
    this.greekSubscriptions = new Map();

    this.debugEnabled = true;
  }

  debug(...args) {
    if (!this.debugEnabled) return;
    console.log("debuggggg [WS-GREEKS]", ...args);
  }

  setDebug(enabled) {
    this.debugEnabled = !!enabled;
  }

  getSharedData() {
    try {
      return JSON.parse(localStorage.getItem("sharedData") || "null");
    } catch (_) {
      return null;
    }
  }

  getSocketState() {
    return this.ws?.readyState;
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect() {
    const shared = this.getSharedData();
    if (!shared?.accessToken) {
      const err = new Error("Missing accessToken in sharedData");
      this.emitError(err);
      this.debug("connect aborted: missing accessToken");
      return;
    }

    this.accessToken = shared.accessToken;
    this.shouldReconnect = true;

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.debug("connect skipped: socket already open");
      return;
    }
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      this.debug("connect skipped: socket already connecting");
      return;
    }

    this.openSocket();
  }

  disconnect({ manual = true } = {}) {
    this.shouldReconnect = !manual;
    this.clearTimers();

    if (this.ws) {
      try {
        this.ws.close(1000, manual ? "manual disconnect" : "disconnect");
      } catch (_) {}
    }

    this.ws = null;
    this.emitStatus({
      type: "disconnected",
      manual,
      readyState: WebSocket.CLOSED,
    });
  }

  subscribeOptionGreek(instruments = []) {
    const normalized = this.normalizeInstrumentIds(instruments);
    if (!normalized.length) return;

    normalized.forEach((id) => {
      this.greekSubscriptions.set(id, id);
    });

    if (this.isConnected()) {
      this.sendOptionGreek("Subscribe", normalized);
    } else {
      this.debug("queued option greek subscription", normalized.length);
      this.connect();
    }
  }

  unsubscribeOptionGreek(instruments = []) {
    const normalized = this.normalizeInstrumentIds(instruments);
    if (!normalized.length) return;

    normalized.forEach((id) => {
      this.greekSubscriptions.delete(id);
    });

    if (this.isConnected()) {
      this.sendOptionGreek("Unsubscribe", normalized);
    }
  }

  onStatus(listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onGreeks(listener) {
    this.greeksListeners.add(listener);
    return () => this.greeksListeners.delete(listener);
  }

  onError(listener) {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  openSocket() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();

    const token = encodeURIComponent(this.accessToken);
    const url = `wss://gateway.5paisa.com/openapi/greeks?access_token=${token}`;
    this.debug("opening greeks socket", {
      attempt: this.reconnectAttempts + 1,
    });

    this.ws = new WebSocket(url);
    this.emitStatus({
      type: "connecting",
      readyState: this.ws.readyState,
      attempt: this.reconnectAttempts + 1,
    });

    this.connectTimeout = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        this.debug("connect timeout -> closing connecting socket");
        try {
          this.ws.close();
        } catch (_) {}
      }
    }, this.connectTimeoutMs);

    this.ws.onopen = () => {
      this.clearConnectTimeout();
      this.reconnectAttempts = 0;
      this.debug("greeks socket open");
      this.emitStatus({ type: "connected", readyState: this.ws.readyState });
      this.replaySubscriptions();
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = (event) => {
      this.debug("greeks socket error");
      this.emitError(event);
    };

    this.ws.onclose = (event) => {
      this.clearConnectTimeout();

      this.debug("greeks socket close", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      this.emitStatus({
        type: "closed",
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      if (!this.shouldReconnect) return;
      this.scheduleReconnect();
    };
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.debug("max reconnect attempts reached");
      this.emitStatus({ type: "reconnect-giveup" });
      return;
    }

    const attempt = this.reconnectAttempts + 1;
    const delay = Math.min(
      this.maxReconnectDelayMs,
      this.baseReconnectDelayMs * 2 ** this.reconnectAttempts,
    );

    this.reconnectAttempts += 1;
    this.debug("scheduling greeks reconnect", { attempt, delay });
    this.emitStatus({ type: "reconnecting", attempt, delay });

    this.reconnectTimeout = setTimeout(() => {
      this.openSocket();
    }, delay);
  }

  handleMessage(rawData) {
    let parsed;
    try {
      parsed = JSON.parse(rawData);
    } catch (err) {
      this.emitError(err);
      return;
    }

    this.greeksListeners.forEach((cb) => {
      try {
        cb(parsed);
      } catch (_) {}
    });
  }

  sendOptionGreek(method, instrumentIds) {
    if (!this.isConnected() || !instrumentIds.length) return;
    const payload = {
      Method: method,
      Operation: "optiongreek",
      instruments: instrumentIds,
    };
    this.ws.send(JSON.stringify(payload));
    this.debug("option greek sent", { method, count: instrumentIds.length });
  }

  replaySubscriptions() {
    const ids = Array.from(this.greekSubscriptions.keys());
    if (ids.length) this.sendOptionGreek("Subscribe", ids);
    this.debug("replayed greek subscriptions", { count: ids.length });
  }

  normalizeInstrumentIds(instruments) {
    const list = instruments || [];
    const out = [];
    const seen = new Set();

    for (const raw of list) {
      const id =
        typeof raw === "string"
          ? raw.trim()
          : String(
              raw?.instrument ??
                raw?.Instrument ??
                raw?.instruments ??
                raw?.id ??
                "",
            ).trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  }

  clearConnectTimeout() {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  clearTimers() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();
  }

  emitStatus(payload) {
    this.statusListeners.forEach((cb) => {
      try {
        cb(payload);
      } catch (_) {}
    });
  }

  emitError(error) {
    this.errorListeners.forEach((cb) => {
      try {
        cb(error);
      } catch (_) {}
    });
  }
}

const fivePaisaOptionGreeksWebSocketService =
  new FivePaisaOptionGreeksWebSocketService();

export default fivePaisaOptionGreeksWebSocketService;
